import { useNavigate } from "react-router";

import {
  Armchair,
  Building2,
  Calendar,
  Clock,
  ExternalLink,
  Eye,
  EyeOff,
  FlaskConical,
  Globe,
  MapPin,
  Phone,
  Users,
} from "lucide-react";

import {
  EnrichmentAsyncHint,
  shouldSuppressEnrichmentLink,
} from "@/components/common/enrichment-async-hint";
import { VENUE_FEATURE_LABELS } from "@/components/enrichment/config";
import { EnrichmentSummary } from "@/components/enrichment/enrichment-summary";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { EntityTinaTrace } from "@/pages/tina/components/entity-tina-trace";
import { VenueViewType } from "@/pages/venues/schemas/venue";

const statusVariant: Record<
  VenueViewType["status"],
  "default" | "secondary" | "destructive" | "outline"
> = {
  Complete: "default",
  Registered: "secondary",
  Requested: "outline",
  Unregistered: "destructive",
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function VenuePreview({
  venue,
  justCreated = false,
}: {
  venue: VenueViewType;
  /** `?created=1` off the post-create redirect. See `enrichment-async-hint`. */
  justCreated?: boolean;
}) {
  const photo = venue.media.photos[0];
  const navigate = useNavigate();
  const needsReview = venue.enrichmentStatus === "NeedsReview";
  const notEnriched =
    !venue.enrichmentStatus || venue.enrichmentStatus === "Unavailable";
  // See ArtistPreview: `Unavailable` is the resting state of a Tina-created
  // venue until its enrichment tail runs, and the button below
  // links to the review page rather than queueing anything, so a stalled
  // `InProgress` must not take it away.
  const hideEnrichmentLink = shouldSuppressEnrichmentLink(
    venue.enrichmentStatus,
    justCreated,
  );

  return (
    <aside className="flex w-1/4 min-w-[260px] flex-col gap-4 border-l border-border bg-card p-5 rounded-l-2xl max-h-[calc(100vh-108px)] sticky top-0 overflow-y-auto">
      {/* Header: photo + name + status */}
      <div className="flex items-start gap-3">
        {photo ? (
          <img
            src={photo.url}
            alt={venue.name}
            width={56}
            height={56}
            className="rounded-md object-cover"
          />
        ) : (
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
            <Building2 className="h-5 w-5" />
          </div>
        )}
        <div className="flex min-w-0 flex-col gap-1">
          <h2 className="truncate text-sm font-semibold text-foreground">
            {venue.name}
          </h2>
          <div className="flex items-center gap-2">
            <Badge variant={statusVariant[venue.status]}>{venue.status}</Badge>
            {venue.hide ? (
              <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <Eye className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </div>
        </div>
      </div>

      <Separator />

      {/* Stats row */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" />
          {venue.followers.toLocaleString()}
        </span>
        <span className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5" />
          {formatDate(venue.createdAt)}
        </span>
      </div>

      {/* Type + Area */}
      <div className="flex flex-wrap gap-1.5">
        <Badge variant="secondary" className="text-xs">
          {venue.type}
        </Badge>
        <Badge variant="outline" className="text-xs">
          {venue.area}
        </Badge>
      </div>

      {/* Address */}
      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Address
        </span>
        <div className="flex items-start gap-1.5 text-sm text-foreground">
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="leading-relaxed">{venue.address}</span>
        </div>
        {venue.geodata?.name && (
          <span className="text-xs text-muted-foreground">
            {[venue.geodata.name, venue.geodata.country]
              .filter(Boolean)
              .join(", ")}
          </span>
        )}
      </div>

      {/* About */}
      {venue.about && (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            About
          </span>
          <p className="line-clamp-4 text-sm leading-relaxed text-foreground">
            {venue.about}
          </p>
        </div>
      )}

      {/* AI Enrichment */}
      {(venue.enrichmentStatus || justCreated) && (
        <>
          <Separator />
          <div className="flex flex-col gap-2">
            <EnrichmentSummary
              resource="venues"
              id={venue.id}
              status={venue.enrichmentStatus}
              enrichment={venue.enrichment}
              featureLabels={VENUE_FEATURE_LABELS}
              showLink={false}
            />
            <EnrichmentAsyncHint
              status={venue.enrichmentStatus}
              justCreated={justCreated}
            />

            {!hideEnrichmentLink && (
              <Button
                size="sm"
                variant={needsReview ? "default" : "outline"}
                className={cn("w-full gap-1.5", needsReview && "animate-pulse")}
                onClick={() => navigate(`/venues/enrichment/${venue.id}`)}
              >
                <FlaskConical className="h-3.5 w-3.5" />
                {needsReview
                  ? "Review Enrichment"
                  : notEnriched
                    ? "Enrich"
                    : "View Enrichment"}
              </Button>
            )}
          </div>
        </>
      )}

      <Separator />

      {/* Contact & links */}
      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Details
        </span>
        <ul className="flex flex-col gap-1.5">
          {venue.phoneNumber && (
            <li className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Phone className="h-3 w-3" />
              <span className="text-foreground">{venue.phoneNumber}</span>
            </li>
          )}
          {venue.capacity != null && (
            <li className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Armchair className="h-3 w-3" />
              <span className="text-foreground">
                {venue.capacity.toLocaleString("en-US")} seats
              </span>
            </li>
          )}
          <li className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span className="text-foreground">{venue.timezone}</span>
          </li>
          <li className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Globe className="h-3 w-3" />
            <span className="font-mono text-xs text-foreground">
              {venue.geolocation.latitude.toFixed(4)},{" "}
              {venue.geolocation.longitude.toFixed(4)}
            </span>
          </li>
          {venue.mapsUrl && (
            <li>
              <a
                href={venue.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                <ExternalLink className="h-3 w-3" />
                Google Maps
              </a>
            </li>
          )}
          {venue.metadata?.wikidataId && (
            <li>
              <a
                href={`https://www.wikidata.org/wiki/${encodeURIComponent(venue.metadata.wikidataId)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                <ExternalLink className="h-3 w-3" />
                Wikidata {venue.metadata.wikidataId}
              </a>
            </li>
          )}
        </ul>
      </div>

      {/* Which Tina run created this venue, or which job last enriched it.
          A venue carries no `metadata.id`, so there was previously no way to
          get from a Tina-created venue back to the walk that made it. */}
      <EntityTinaTrace
        entityType="venue"
        entityId={venue.id}
        metadata={venue.metadata}
        enrichment={venue.enrichment}
      />

      {/* ID + createdBy footer */}
      <div className="mt-auto flex flex-col gap-0.5 text-xs text-muted-foreground">
        <span className="truncate font-mono">{venue.id}</span>
        {venue.slug && (
          <span>
            slug:{" "}
            <span className="font-mono text-foreground">{venue.slug}</span>
          </span>
        )}
        {venue.createdBy && <span>by {venue.createdBy}</span>}
      </div>
    </aside>
  );
}
